// Angular Files
import {
 DOCUMENT, Inject, Injectable, signal
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Teller Online Files
import { RumPortal } from '../rum/rum.models';
import { TellerOnlineRumService } from '../rum/rum.service';

// Component Files
import { GuideDefinition } from './engagement.models';
import { TellerOnlineGuideService } from './guide.service';
import { TellerOnlineSurveyService } from './survey.service';

/** Delay so the page the guide points at has rendered. */
const AUTO_START_DELAY_MS = 800;

/** Delay so the drawer a replay was chosen from has closed. */
const REPLAY_DELAY_MS = 350;

/**
 * Entry point for guides and surveys. Inert unless RUM is enabled (Demo and local), so other
 * environments are unaffected. For demos, `?guide=<id>` / `?survey=<id>` force one to show, and
 * `tellerOnlineEngagement` on `window` offers the same from the console.
 */
@Injectable({ providedIn: 'root' })
export class TellerOnlineEngagementService {
    private _enabled = signal(false);

    readonly enabled = this._enabled.asReadonly();

    private _portal: RumPortal = 'public';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private rumService: TellerOnlineRumService,
        private guideService: TellerOnlineGuideService,
        private surveyService: TellerOnlineSurveyService
    ) { }

    public get portal(): RumPortal {
        return this._portal;
    }

    /** Call after TellerOnlineRumBootstrapService.start. */
    public start(portal: RumPortal): void {
        if (this._enabled() || !this.rumService.enabled) return;

        this._portal = portal;
        this._enabled.set(true);
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) this._onNavigated(event.urlAfterRedirects);
        });
        this._exposeDebugApi();
    }

    /** Lets a page announce something a survey can trigger on, e.g. `payment_success`. */
    public notify(eventName: string): void {
        if (this._enabled()) this.surveyService.triggerEvent(eventName, this._portal);
    }

    /** Guides of this portal, for the help sidebar's replay entries. */
    public get guides(): GuideDefinition[] {
        return this.guideService.definitions.filter(g => g.portal === this._portal);
    }

    /** Starts a guide regardless of whether it was seen; used by the help sidebar. */
    public replayGuide(id: string): void {
        // Lets the help drawer finish closing, so focus has somewhere sensible to return to
        const begin = () => setTimeout(() => this.guideService.start(id, true), REPLAY_DELAY_MS);
        const guide = this.guides.find(g => g.id === id);
        const path = this.router.url.split(/[?#]/)[0];

        // The steps point at that page's elements, so take the user there first
        const onPage = guide?.autoStartPath?.test(path) ?? path === guide?.startUrl;
        if (guide?.startUrl && !onPage) {
            this.router.navigateByUrl(guide.startUrl)
                .then(navigated => navigated && begin())
                .catch(err => console.warn('Guide replay navigation failed', err));
        } else {
            begin();
        }
    }

    private _onNavigated(url: string): void {
        const [path, query = ''] = url.split('#')[0].split('?');
        const params = new URLSearchParams(query.split('#')[0]);

        this.surveyService.countPageView(this._portal);
        setTimeout(() => this.guideService.autoStart(path, this._portal), AUTO_START_DELAY_MS);

        const guideId = params.get('guide');
        const surveyId = params.get('survey');
        if (guideId) {
            setTimeout(() => this.guideService.start(guideId, true), AUTO_START_DELAY_MS);
        }
        if (surveyId) {
            setTimeout(() => this.surveyService.force(surveyId), AUTO_START_DELAY_MS);
        }
    }

    private _exposeDebugApi(): void {
        const win = this.document.defaultView as unknown as Record<string, unknown> | null;
        if (!win) return;

        win.tellerOnlineEngagement = {
            guides: () => this.guideService.definitions.map(g => g.id),
            surveys: () => this.surveyService.definitions.map(s => s.id),
            guide: (id: string) => this.guideService.start(id, true),
            survey: (id: string) => this.surveyService.force(id),
            reset: () => {
                this.guideService.reset();
                this.surveyService.reset();
            }
        };
    }
}
